import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  WorkflowEmailRecipientInfo,
  WORKFLOW_EMAIL_RECIPIENT_KIND_LABELS,
  WORKFLOW_EMAIL_RECIPIENT_SOURCE_LABELS
} from '../models/workflow-action.model';

/**
 * Lista de destinatarios REALMENTE resueltos por el backend para una acción de email
 * (vista previa, probador de acciones y salida del paso ejecutado).
 *
 * Es lo que hace manejable la configuración: el usuario ve a quién le va a llegar el correo
 * —y por qué motivo entró cada uno— antes de activar el workflow. Acepta el valor crudo del
 * `output`/`response` (`unknown`) porque en la salida del paso viaja dentro de un mapa suelto.
 */
@Component({
  selector: 'app-workflow-recipient-list',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (recipients().length) {
      <div class="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-700/30">
        <p class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Destinatarios ({{ recipients().length }})
        </p>
        <ul class="space-y-1.5">
          @for (r of recipients(); track $index) {
            <li class="flex items-start gap-2 text-xs">
              <span [class]="kindBadgeClass">{{ kindLabel(r) }}</span>
              <span class="min-w-0 flex-1">
                <span class="block truncate font-medium text-gray-800 dark:text-gray-100">
                  {{ r.name || r.email }}
                </span>
                @if (r.name && r.email) {
                  <span class="block truncate text-gray-500 dark:text-gray-400">{{ r.email }}</span>
                }
                <span class="block text-[11px] text-gray-400 dark:text-gray-500">{{ sourceLabel(r) }}</span>
              </span>
            </li>
          }
        </ul>
      </div>
    }
  `
})
export class WorkflowRecipientListComponent {
  /** Valor crudo de `recipients` (respuesta de preview/test o `output.recipients` del paso). */
  readonly value = input<unknown>(null);

  protected readonly kindBadgeClass =
    'mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase text-gray-600 ring-1 ring-gray-300 dark:text-gray-300 dark:ring-gray-600';

  protected readonly recipients = computed<WorkflowEmailRecipientInfo[]>(() => {
    const raw = this.value();
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .filter((item): item is WorkflowEmailRecipientInfo => !!item && typeof item === 'object')
      .filter((item) => !!item.email);
  });

  protected kindLabel(recipient: WorkflowEmailRecipientInfo): string {
    const kind = recipient.kind ?? 'TO';
    return WORKFLOW_EMAIL_RECIPIENT_KIND_LABELS[kind] ?? kind;
  }

  protected sourceLabel(recipient: WorkflowEmailRecipientInfo): string {
    const source = recipient.source ?? '';
    return WORKFLOW_EMAIL_RECIPIENT_SOURCE_LABELS[source] ?? 'Destinatario';
  }
}
